import type { Session } from "@fluentry/domain";
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@fluentry/ui";
import { Link, Outlet } from "@tanstack/react-router";
import {
  ChevronRight,
  CircleHelp,
  CreditCard,
  LayoutGrid,
  LogOut,
  Menu,
  Settings,
  User,
  Users2,
} from "lucide-react";
import React from "react";
import type { AuthSessionData } from "../context/app-context";
import type { AppShellPageMeta } from "../hooks/use-app-shell-route-state";
import {
  DesktopNavigationSidebar,
  MobileBottomNav,
  MobileNavigationDrawer,
} from "./navigation-shell";
import { RouteFallback } from "./route-fallback";

type AppShellFrameProps = {
  authSession: AuthSessionData | null;
  isSettingsRoute: boolean;
  mobileNavOpen: boolean;
  onCloseMobileNav: () => void;
  onCreateDraft: () => Promise<void>;
  onNavigateToSection: (section: "drafts" | "history") => Promise<void>;
  onRefetchSession: () => Promise<void>;
  onOpenMobileNav: () => void;
  onSignOut: () => Promise<void>;
  pageMeta: AppShellPageMeta;
  sectionLabel: string;
  session: Session;
  sessionActionError?: string | null;
  shellSurfaceClass: string;
};

export function AppShellFrame({
  authSession,
  isSettingsRoute,
  mobileNavOpen,
  onCloseMobileNav,
  onCreateDraft,
  onNavigateToSection,
  onRefetchSession,
  onOpenMobileNav,
  onSignOut,
  pageMeta,
  sectionLabel,
  session,
  sessionActionError,
  shellSurfaceClass,
}: AppShellFrameProps) {
  function renderSessionBadge(triggerClassName: string, guestClassName: string) {
    if (!session.authenticated) {
      return (
        <Badge variant="outline" className={guestClassName}>
          Guest
        </Badge>
      );
    }

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" className={triggerClassName} aria-label="Open user menu">
            {session.user?.name?.charAt(0).toUpperCase() ?? <User className="h-3.5 w-3.5" />}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="font-normal">
            <div className="grid gap-0.5">
              <span className="text-sm font-semibold text-foreground">{session.user?.name}</span>
              <span className="truncate text-xs text-muted-foreground">{session.user?.email}</span>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link to="/settings/profile" className="flex cursor-pointer items-center">
              <User className="mr-2 h-4 w-4" />
              Profile
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/settings/security" className="flex cursor-pointer items-center">
              <Settings className="mr-2 h-4 w-4" />
              Security
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/settings/billing" className="flex cursor-pointer items-center">
              <CreditCard className="mr-2 h-4 w-4" />
              Billing
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/settings/organizations" className="flex cursor-pointer items-center">
              <Users2 className="mr-2 h-4 w-4" />
              Organizations
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/help" className="flex cursor-pointer items-center">
              <CircleHelp className="mr-2 h-4 w-4" />
              Help
            </Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            variant="destructive"
            className="cursor-pointer"
            onSelect={() => void onSignOut()}
          >
            <LogOut className="mr-2 h-4 w-4" />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <main className={`mobile-safe-shell min-h-dvh ${shellSurfaceClass} text-foreground`}>
      <div className="grid min-h-dvh sm:grid-cols-[220px_minmax(0,1fr)] md:grid-cols-[248px_minmax(0,1fr)] lg:grid-cols-[272px_minmax(0,1fr)] xl:grid-cols-[288px_minmax(0,1fr)]">
        <aside className="hidden sm:flex sm:flex-col sm:border-r sm:border-border/80 sm:bg-card/65 sm:px-4 sm:pb-5 sm:text-foreground sm:backdrop-blur-xl md:px-4 lg:px-5 xl:px-6">
          <DesktopNavigationSidebar
            session={session}
            authSession={authSession}
            onSignOut={onSignOut}
            onNavigateToSection={onNavigateToSection}
            onCreateDraft={onCreateDraft}
            onRefetchSession={onRefetchSession}
          />
        </aside>

        <section className="flex min-h-0 min-w-0 flex-col sm:min-h-dvh">
          <header className="sticky top-0 z-10 border-b border-border/80 bg-background/88 backdrop-blur-xl">
            <div className="px-4 py-3 sm:px-5 sm:py-4 md:px-6 lg:px-7 xl:px-8">
              <div className="grid gap-3 sm:hidden">
                {/* Keep mobile chrome in two rows so navigation, section context, and page heading stay readable. */}
                <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 text-muted-foreground">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-11 w-11 rounded-full border-border bg-card text-primary shadow-none hover:bg-accent"
                    onClick={onOpenMobileNav}
                    aria-label="Open navigation menu"
                  >
                    <Menu className="h-4 w-4" />
                  </Button>
                  <div className="inline-flex min-w-0 items-center gap-2 rounded-full border border-border/80 bg-card/90 px-3 py-2 text-[10px] font-semibold tracking-[0.18em] text-muted-foreground uppercase shadow-sm">
                    <LayoutGrid className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{sectionLabel}</span>
                  </div>
                  {renderSessionBadge(
                    "flex h-11 w-11 items-center justify-center rounded-full border border-border bg-primary text-[11px] font-semibold text-primary-foreground shadow-sm transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    "flex h-11 min-w-11 items-center justify-center rounded-full border border-border bg-card/90 px-3 text-[10px] font-semibold tracking-[0.18em] text-muted-foreground uppercase shadow-none",
                  )}
                </div>
                <div className="grid gap-1">
                  <h1 className="font-serif text-[1.65rem] leading-none tracking-tight text-foreground">
                    {pageMeta.title}
                  </h1>
                  <p className="max-w-[34ch] text-sm leading-5 text-muted-foreground">
                    {pageMeta.subtitle}
                  </p>
                </div>
              </div>

              <div className="hidden flex-wrap items-center justify-between gap-4 sm:flex">
                <div className="flex min-w-0 items-center gap-2 text-muted-foreground">
                  <div className="flex items-center gap-2 text-[11px] font-semibold tracking-[0.22em] uppercase">
                    <LayoutGrid className="h-3.5 w-3.5" />
                    <span>{sectionLabel}</span>
                  </div>
                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/70" />
                  <div className="grid gap-0.5">
                    <h1 className="font-serif text-xl leading-none text-foreground">
                      {pageMeta.title}
                    </h1>
                    <p className="text-xs text-muted-foreground">{pageMeta.subtitle}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {renderSessionBadge(
                    "flex h-8 w-8 items-center justify-center rounded-full border border-border bg-primary text-[11px] font-semibold text-primary-foreground shadow-sm transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    "flex h-8 min-w-8 items-center justify-center rounded-full border border-border bg-card/90 px-2 text-[10px] font-semibold tracking-[0.18em] text-muted-foreground uppercase shadow-none",
                  )}
                </div>
              </div>
            </div>
          </header>

          <div
            className={`flex-1 px-4 py-4 sm:px-5 ${isSettingsRoute ? "md:px-7 md:py-7 lg:px-8 xl:px-10" : "md:px-6 md:py-6 lg:px-7 xl:px-8"}`}
          >
            <div className={`mx-auto ${isSettingsRoute ? "max-w-[1240px]" : "max-w-[1480px]"}`}>
              {sessionActionError ? (
                <div
                  role="alert"
                  className="mb-4 rounded-md border border-destructive/35 bg-destructive/10 px-4 py-3 text-sm text-destructive"
                >
                  {sessionActionError}
                </div>
              ) : null}
              <React.Suspense fallback={<RouteFallback />}>
                <Outlet />
              </React.Suspense>
            </div>
            {/* Clear the fixed mobile tab bar so trailing content never hides behind it; track the safe-area inset. */}
            <div
              className="sm:hidden"
              style={{ height: "calc(5.5rem + var(--safe-area-bottom))" }}
              aria-hidden="true"
            />
          </div>
        </section>
      </div>
      <MobileBottomNav
        session={session}
        onNavigateToSection={onNavigateToSection}
        onCreateDraft={onCreateDraft}
      />
      <MobileNavigationDrawer
        open={mobileNavOpen}
        session={session}
        authSession={authSession}
        onSignOut={onSignOut}
        onNavigateToSection={onNavigateToSection}
        onCreateDraft={onCreateDraft}
        onRefetchSession={onRefetchSession}
        onClose={onCloseMobileNav}
      />
    </main>
  );
}
